import React from 'react';
import LegalLayout from '../components/layout/LegalLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiFileText, 
  FiAlertTriangle, 
  FiUserCheck, 
  FiShield, 
  FiCpu, 
  FiAward, 
  FiGlobe, 
  FiMail,
  FiScale,
  FiBookOpen,
  FiHeart
} = FiIcons;

const navLinks = [
  { label: 'Terms at a Glance', href: '#summary' },
  { label: '1. Acceptance', href: '#acceptance' },
  { label: '2. Disclaimers', href: '#disclaimers' },
  { label: '3. Your Account', href: '#account' },
  { label: '4. Acceptable Use', href: '#use' },
  { label: '5. Intellectual Property', href: '#ip' },
  { label: '6. Liability', href: '#liability' },
  { label: '7. Governing Law', href: '#law' },
  { label: '8. Contact', href: '#contact' },
];

const SectionIcon = ({ icon, className = "text-rose bg-paper border-line" }) => (
  <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center mb-6 shadow-sm ${className}`}>
    <SafeIcon icon={icon} className="w-6 h-6" />
  </div>
);

const Terms = () => {
  return (
    <LegalLayout 
      title="Terms of Use" 
      lastUpdated="19 June 2026" 
      navLinks={navLinks}
    >
      <div className="mb-16">
        <p className="text-xl text-muted leading-relaxed font-light">
          Welcome to Nisaa. These Terms of Use form a binding legal agreement between you and <strong>Sussex Endocrine Clinic</strong>. They govern your use of the Nisaa mobile application, website, and related services.
        </p>
      </div>

      {/* Terms at a Glance */}
      <section id="summary" className="scroll-mt-32">
        <SectionIcon icon={FiFileText} />
        <h2>Terms at a Glance</h2>
        <p className="mb-8">Legal documents can be dense. Here are the most important rules for using Nisaa:</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-8 not-prose">
          {[
            { 
              title: "Not Medical Advice", 
              desc: "Nisaa is a tracking tool, not a doctor. Seek professional help for medical decisions.",
              icon: FiHeart 
            },
            { 
              title: "Not a Fatwa Service", 
              desc: "We provide educational support, not binding Islamic rulings for your specific situation.",
              icon: FiBookOpen 
            },
            { 
              title: "You Own Your Data", 
              desc: "Your data belongs to you. We just provide the tools to track and optionally back it up.",
              icon: FiShield 
            },
            { 
              title: "UK Jurisdiction", 
              desc: "We are based in the UK, and these terms are governed by the laws of England and Wales.",
              icon: FiGlobe 
            }
          ].map((item, i) => (
            <div key={i} className="p-6 rounded-[24px] bg-white border border-line/60 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3 text-rose">
                <SafeIcon icon={item.icon} className="w-5 h-5" />
                <span className="font-bold text-aubergine">{item.title}</span>
              </div>
              <p className="text-sm text-muted leading-relaxed m-0">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 1. Acceptance */}
      <section id="acceptance" className="scroll-mt-32">
        <SectionIcon icon={FiUserCheck} />
        <h2>1. Acceptance & Eligibility</h2>
        <p>
          By downloading, installing, accessing, or using Nisaa, you agree to these Terms. If you do not agree, you must immediately delete the app and cease using our services.
        </p>
        <p>
          <strong>Age Requirement:</strong> You must be at least 13 years old (or 16 where UK/EU/EEA rules require it) to use Nisaa. If you are under the age of majority where you live, you confirm that you have permission from a parent or guardian.
        </p>
      </section>

      {/* 2. Disclaimers */}
      <section id="disclaimers" className="scroll-mt-32">
        <SectionIcon icon={FiAlertTriangle} className="text-rose bg-rose/10 border-rose/20" />
        <h2>2. Important Disclaimers</h2>
        
        <div className="my-8 p-6 border-l-4 border-rose bg-rose/5 rounded-r-3xl">
          <h3 className="m-0 font-bold text-aubergine text-lg mb-2">2.1 Not Medical Advice</h3>
          <p className="m-0 text-muted">
            Nisaa provides general wellness tracking for cycles, pregnancy, and fasting. <strong>Nisaa is not a medical device.</strong> It does not provide medical advice, diagnosis, treatment, contraception advice, fertility guarantees, or emergency care. Always consult a qualified healthcare professional for medical questions, unusual symptoms, or before making health decisions.
          </p>
        </div>

        <h3>2.2 Religious Guidance Limitations</h3>
        <p>
          Nisaa provides educational content regarding Islamic practice (e.g., prayer times, ghusl, fasting). This content is informational only and does not constitute a formal religious ruling (fatwa). For binding rulings, please consult a qualified scholar familiar with your specific madhab, local practice, and personal circumstances.
        </p>
      </section>

      {/* 3. Your Account */}
      <section id="account" className="scroll-mt-32">
        <SectionIcon icon={FiShield} />
        <h2>3. Your Account and Data</h2>
        <p>
          You may use most of Nisaa's features locally without an account. If you choose to create an account for cloud backups:
        </p>
        <ul>
          <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
          <li>You are responsible for all activities that occur under your account.</li>
          <li>You retain full ownership of all data you enter into the app.</li>
        </ul>
        <p>
          You may delete your account and all associated cloud data at any time via the app settings. For more details on how we handle your data, please review our <a href="/#/privacy">Privacy Policy</a>.
        </p>
      </section>

      {/* 4. Acceptable Use */}
      <section id="use" className="scroll-mt-32">
        <SectionIcon icon={FiCpu} />
        <h2>4. Acceptable Use</h2>
        <p>We grant you a limited, non-exclusive, non-transferable, revocable licence to use Nisaa for your personal, non-commercial use. You agree <strong>not</strong> to:</p>
        <ul>
          <li>Reverse engineer, decompile, or disassemble the app.</li>
          <li>Attempt to gain unauthorized access to our servers or databases.</li>
          <li>Use Nisaa to distribute malware, spam, or unlawful content.</li>
          <li>Use the app to provide medical or religious advice to third parties as though it came from a qualified professional.</li>
        </ul>
      </section>

      {/* 5. Intellectual Property */}
      <section id="ip" className="scroll-mt-32">
        <SectionIcon icon={FiAward} />
        <h2>5. Intellectual Property</h2>
        <p>
          The Nisaa name, logo, app design, code, written content, and original illustrations are the exclusive property of Sussex Endocrine Clinic and are protected by UK and international copyright laws. You may not copy, modify, or distribute our intellectual property without our express written consent.
        </p>
      </section>

      {/* 6. Liability */}
      <section id="liability" className="scroll-mt-32">
        <SectionIcon icon={FiScale} />
        <h2>6. Limitation of Liability</h2>
        <p>
          Nisaa is provided on an "as is" and "as available" basis without warranties of any kind. We do not guarantee that predictions, reminders, or calculations will be 100% accurate, uninterrupted, or error-free.
        </p>
        <p>
          To the maximum extent permitted by applicable law, Sussex Endocrine Clinic will not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data or emotional distress, arising from your use of the app. In no event shall our total aggregate liability exceed the amount you paid us in the past 12 months, or £50 GBP, whichever is greater.
        </p>
      </section>

      {/* 7. Governing Law */}
      <section id="law" className="scroll-mt-32">
        <SectionIcon icon={FiGlobe} />
        <h2>7. Governing Law and Disputes</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of <strong>England and Wales</strong>. Any disputes arising out of or relating to these Terms or your use of Nisaa shall be subject to the exclusive jurisdiction of the courts of England and Wales, unless mandatory consumer protection laws in your country of residence dictate otherwise.
        </p>
      </section>

      {/* 8. Contact */}
      <section id="contact" className="scroll-mt-32">
        <SectionIcon icon={FiMail} />
        <h2>8. Contact Us</h2>
        <div className="p-8 rounded-[32px] bg-aubergine text-white not-prose relative overflow-hidden mt-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-rose/10 rounded-full blur-3xl -mr-16 -mt-16" />
          <p className="text-white/70 mb-6 relative z-10">
            If you have any questions, concerns, or feedback regarding these Terms of Use, please reach out to us:
          </p>
          <div className="relative z-10">
            <p className="font-bold text-xl mb-1">Sussex Endocrine Clinic</p>
            <p className="text-white/60 text-sm mb-6">United Kingdom</p>
            <a 
              href="mailto:contact@sussexendocrineclinic.co.uk" 
              className="inline-flex items-center gap-2 text-rose hover:text-white transition-colors font-bold text-lg"
            >
              contact@sussexendocrineclinic.co.uk
              <SafeIcon icon={FiIcons.FiArrowRight} className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
};

export default Terms;